/**
 * ResetPasswordPage — Sichere Passwort-Zurücksetzen-Seite
 * 
 * Wird aufgerufen wenn der User auf den Reset-Link in der E-Mail klickt.
 * Supabase setzt automatisch eine RECOVERY-Session → User kann neues Passwort setzen.
 * 
 * SICHERHEIT:
 * - Kein Auto-Login: User wird NICHT automatisch eingeloggt
 * - Passwort-Validierung: Min. 8 Zeichen, Groß-/Kleinbuchstaben, Zahl
 * - Token wird nach Verwendung ungültig (Supabase-Standard)
 * - Nach erfolgreichem Reset → Logout + Redirect zu Login
 */

import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { Eye, EyeOff, Lock, CheckCircle2, AlertCircle, Loader2, ShieldCheck } from 'lucide-react';

// Passwort-Stärke prüfen
function validatePassword(pw: string): { valid: boolean; errors: string[] } {
  const errors: string[] = [];
  if (pw.length < 8) errors.push('Mindestens 8 Zeichen');
  if (!/[A-Z]/.test(pw)) errors.push('Mindestens ein Großbuchstabe');
  if (!/[a-z]/.test(pw)) errors.push('Mindestens ein Kleinbuchstabe');
  if (!/[0-9]/.test(pw)) errors.push('Mindestens eine Zahl');
  return { valid: errors.length === 0, errors };
}

export default function ResetPasswordPage() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionReady, setSessionReady] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);

  // Prüfe ob eine gültige RECOVERY-Session existiert
  useEffect(() => {
    const checkSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          setSessionReady(true);
        } else {
          // Warte kurz — Supabase braucht manchmal einen Moment um den Token zu verarbeiten
          await new Promise(r => setTimeout(r, 1500));
          const { data: { session: retrySession } } = await supabase.auth.getSession();
          setSessionReady(!!retrySession);
          if (!retrySession) {
            setError('Der Reset-Link ist ungültig oder abgelaufen. Bitte fordern Sie einen neuen Link an.');
          }
        }
      } catch {
        setError('Fehler beim Prüfen der Session.');
      } finally {
        setCheckingSession(false);
      }
    };

    // Supabase onAuthStateChange für RECOVERY-Event
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY' && session) {
        setSessionReady(true);
        setCheckingSession(false);
      }
    });

    checkSession();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const validation = validatePassword(password);
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!validation.valid) {
      setError('Passwort erfüllt nicht die Anforderungen.');
      return;
    }

    if (!passwordsMatch) {
      setError('Passwörter stimmen nicht überein.');
      return;
    }

    setLoading(true);
    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password,
      });

      if (updateError) {
        setError(updateError.message);
        return;
      }

      setSuccess(true);

      // Nach 3 Sekunden: Ausloggen und zur Login-Seite weiterleiten
      setTimeout(async () => {
        await supabase.auth.signOut();
        window.location.href = '/login';
      }, 3000);
    } catch (err: any) {
      setError(err?.message || 'Ein unbekannter Fehler ist aufgetreten.');
    } finally {
      setLoading(false);
    }
  };

  // Loading-State während Session geprüft wird
  if (checkingSession) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md text-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#B91C1C] mx-auto mb-4" />
          <p className="text-gray-600">Reset-Link wird geprüft...</p>
        </div>
      </div>
    );
  }

  // Erfolgs-Anzeige
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Passwort erfolgreich geändert</h2>
          <p className="text-gray-600 mb-4">
            Ihr Passwort wurde aktualisiert. Sie werden jetzt zur Login-Seite weitergeleitet...
          </p>
          <Loader2 className="w-5 h-5 animate-spin text-gray-400 mx-auto" />
        </div>
      </div>
    );
  }

  // Ungültiger/abgelaufener Link
  if (!sessionReady) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Link ungültig</h2>
          <p className="text-gray-600 mb-6">
            {error || 'Der Reset-Link ist ungültig oder abgelaufen.'}
          </p>
          <a
            href="/login"
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#B91C1C] text-white rounded-lg hover:bg-[#991B1B] transition-colors font-medium"
          >
            Zurück zum Login
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-[#B91C1C]/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <ShieldCheck className="w-8 h-8 text-[#B91C1C]" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Neues Passwort setzen</h1>
          <p className="text-gray-500 mt-2">Wählen Sie ein sicheres Passwort für Ihr Konto.</p>
        </div>

        {/* Fehler */}
        {error && (
          <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Neues Passwort */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Neues Passwort</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Mindestens 8 Zeichen"
                className="w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#B91C1C]/20 focus:border-[#B91C1C] transition-colors"
                autoFocus
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>

            {/* Passwort-Anforderungen */}
            {password.length > 0 && (
              <div className="mt-2 space-y-1">
                {[
                  { label: 'Mindestens 8 Zeichen', ok: password.length >= 8 },
                  { label: 'Großbuchstabe (A-Z)', ok: /[A-Z]/.test(password) },
                  { label: 'Kleinbuchstabe (a-z)', ok: /[a-z]/.test(password) },
                  { label: 'Zahl (0-9)', ok: /[0-9]/.test(password) },
                ].map((req) => (
                  <div key={req.label} className="flex items-center gap-2">
                    <div className={`w-4 h-4 rounded-full flex items-center justify-center ${req.ok ? 'bg-green-100' : 'bg-gray-100'}`}>
                      {req.ok ? (
                        <CheckCircle2 className="w-3 h-3 text-green-600" />
                      ) : (
                        <div className="w-1.5 h-1.5 bg-gray-400 rounded-full" />
                      )}
                    </div>
                    <span className={`text-xs ${req.ok ? 'text-green-700' : 'text-gray-500'}`}>{req.label}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Passwort bestätigen */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Passwort bestätigen</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type={showConfirm ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Passwort wiederholen"
                className={`w-full pl-10 pr-10 py-3 border rounded-lg focus:ring-2 focus:ring-[#B91C1C]/20 focus:border-[#B91C1C] transition-colors ${
                  confirmPassword.length > 0 && !passwordsMatch
                    ? 'border-red-300 bg-red-50/50'
                    : confirmPassword.length > 0 && passwordsMatch
                    ? 'border-green-300 bg-green-50/50'
                    : 'border-gray-300'
                }`}
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirm(!showConfirm)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showConfirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            {confirmPassword.length > 0 && !passwordsMatch && (
              <p className="mt-1 text-xs text-red-500">Passwörter stimmen nicht überein</p>
            )}
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={loading || !validation.valid || !passwordsMatch}
            className="w-full py-3 bg-[#B91C1C] text-white rounded-lg font-medium hover:bg-[#991B1B] disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Wird gespeichert...
              </>
            ) : (
              <>
                <ShieldCheck className="w-5 h-5" />
                Passwort ändern
              </>
            )}
          </button>
        </form>

        {/* Zurück zum Login */}
        <div className="mt-6 text-center">
          <a
            href="/login"
            className="text-sm text-gray-500 hover:text-[#B91C1C] transition-colors"
          >
            Zurück zum Login
          </a>
        </div>
      </div>
    </div>
  );
}
